import { useState } from 'react';
import {
  Plus,
  CheckCircle,
  Clock,
  DollarSign,
  Calendar,
  MoreVertical,
  FileText,
  Send,
  AlertTriangle,
} from 'lucide-react';
import { useProjectMilestones, useCreateMilestone, useUpdateMilestone } from '@/hooks/useProjects';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { format } from 'date-fns';
import { toast } from 'sonner';
import type { ProjectMilestoneInsert } from '@/types/project.types';

interface ProjectMilestonesProps {
  projectId: string;
  limit?: number;
}

export default function ProjectMilestones({ projectId, limit }: ProjectMilestonesProps) {
  const { data: milestones, isLoading } = useProjectMilestones(projectId);
  const createMilestone = useCreateMilestone();
  const updateMilestone = useUpdateMilestone();
  const navigate = useNavigate();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newMilestone, setNewMilestone] = useState<Partial<ProjectMilestoneInsert>>({
    project_id: projectId,
    status: 'pending',
  });

  const displayedMilestones = limit ? milestones?.slice(0, limit) : milestones;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-blue-600" />;
      case 'overdue':
        return <Clock className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-600" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleCreateMilestone = async () => {
    if (!newMilestone.milestone_name || !newMilestone.amount) return;

    try {
      await createMilestone.mutateAsync(newMilestone as ProjectMilestoneInsert);
      setIsCreateDialogOpen(false);
      setNewMilestone({
        project_id: projectId,
        status: 'pending',
      });
    } catch (error) {
      // Error handling omitted intentionally
    }
  };

  const handleStatusChange = async (milestoneId: string, status: string) => {
    try {
      await updateMilestone.mutateAsync({
        id: milestoneId,
        update: {
          status: status as any,
          completion_date:
            status === 'completed' ? new Date().toISOString().split('T')[0] : undefined,
        },
      });
    } catch (error) {
      // Error handling omitted intentionally
    }
  };

  const handleGenerateInvoice = (milestone: any) => {
    // Navigate to create invoice page with milestone data pre-filled
    navigate('/invoices/create', {
      state: {
        fromMilestone: true,
        milestoneId: milestone.id,
        projectId: milestone.project_id,
        amount: milestone.amount,
        description: `Payment for milestone: ${milestone.milestone_name}`,
      },
    });
    toast.success('Redirecting to invoice creation...');
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">
          {limit ? 'Upcoming Milestones' : 'Project Milestones'}
        </h3>
        {!limit && (
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="mr-2 h-4 w-4" />
                Add Milestone
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Milestone</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="milestone_name">Milestone Name</Label>
                  <Input
                    id="milestone_name"
                    value={newMilestone.milestone_name || ''}
                    onChange={(e) =>
                      setNewMilestone((prev) => ({ ...prev, milestone_name: e.target.value }))
                    }
                    placeholder="Enter milestone name"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newMilestone.description || ''}
                    onChange={(e) =>
                      setNewMilestone((prev) => ({ ...prev, description: e.target.value }))
                    }
                    placeholder="Enter milestone description"
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="amount">Amount ($)</Label>
                    <Input
                      id="amount"
                      type="number"
                      value={newMilestone.amount || ''}
                      onChange={(e) =>
                        setNewMilestone((prev) => ({ ...prev, amount: parseFloat(e.target.value) }))
                      }
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <Label htmlFor="due_date">Due Date</Label>
                    <Input
                      id="due_date"
                      type="date"
                      value={newMilestone.due_date || ''}
                      onChange={(e) =>
                        setNewMilestone((prev) => ({ ...prev, due_date: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={newMilestone.status}
                    onValueChange={(value) =>
                      setNewMilestone((prev) => ({ ...prev, status: value as any }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateMilestone} disabled={createMilestone.isPending}>
                    Create Milestone
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {displayedMilestones && displayedMilestones.length > 0 ? (
        <div className="space-y-3">
          {displayedMilestones.map((milestone) => (
            <Card key={milestone.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  {getStatusIcon(milestone.status)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">{milestone.milestone_name}</h4>
                      <Badge className={getStatusColor(milestone.status)}>{milestone.status}</Badge>
                    </div>

                    {milestone.description && (
                      <p className="text-sm text-muted-foreground mt-1">{milestone.description}</p>
                    )}

                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        {formatCurrency(milestone.amount)}
                      </div>

                      {milestone.due_date && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Due {format(new Date(milestone.due_date), 'MMM d, yyyy')}
                        </div>
                      )}

                      {milestone.completion_date && (
                        <div className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Completed {format(new Date(milestone.completion_date), 'MMM d, yyyy')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {!limit && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem
                        onClick={() => handleStatusChange(milestone.id, 'in_progress')}
                      >
                        Mark In Progress
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleStatusChange(milestone.id, 'completed')}
                      >
                        Mark Completed
                      </DropdownMenuItem>
                      {milestone.status === 'completed' && !milestone.invoice_id && (
                        <DropdownMenuItem onClick={() => handleGenerateInvoice(milestone)}>
                          <FileText className="mr-2 h-4 w-4" />
                          Generate Invoice
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem>Edit</DropdownMenuItem>
                      <DropdownMenuItem className="text-red-600">Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </Card>
          ))}

          {limit && milestones && milestones.length > limit && (
            <div className="text-center">
              <Button variant="link" className="text-sm">
                View all {milestones.length} milestones →
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8">
          <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No Milestones</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Create milestones to track project progress and payments.
          </p>
          {!limit && (
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add First Milestone
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
